/**
 * `PublicCvUpload` — pagina pubblica di caricamento CV, resa fuori da `Layout`
 * e senza autenticazione (Req. 6.1).
 *
 * Rotta `/public/submission`. Consente a un candidato anonimo di inviare nome,
 * cognome e un CV in PDF senza effettuare il login. L'upload riusa lo schema
 * presigned già adottato dal flusso autenticato dei CV esterni (nessun transito
 * del file attraverso l'API), ma verso gli endpoint pubblici:
 *   1. `POST /api/public/public-cv/uploads/presign { contentType: 'application/pdf' }`
 *      → `uploadUrl` / `key` firmati (Req. 1.1);
 *   2. `PUT` diretto del PDF su S3 verso `uploadUrl` con l'header
 *      `Content-Type` combaciante con la firma;
 *   3. `POST /api/public/public-cv/profiles { firstName, lastName, cvPdfKey }`
 *      → crea il profilo, avvia la pre-compilazione AI e restituisce il
 *      Public_Token (Req. 1.3).
 *
 * Al successo si naviga alla schermata di recap referenziando il profilo
 * tramite il **Public_Token** restituito, mai un identificativo interno
 * (Req. 6.2). Il client API è token-less (`usePublicApiClient`).
 */
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePublicApiClient } from '../../api/usePublicApiClient';
import {
  DEFAULT_PUBLIC_CV_PRIVACY_POLICY_URL,
  type PresignedUpload,
  type PublicCvCreateResult,
  type PublicCvSettingsDto,
} from '../../shared/dto';
import { Alert, Button, Field, PageHeader, TextInput } from '../../components/ui';

/** Content-type dell'unico formato ammesso per il Cv_Pdf. */
const PDF_CONTENT_TYPE = 'application/pdf';

/** Corpo della creazione pubblica (`POST /api/public/public-cv/profiles`). */
interface CreatePublicProfileBody {
  firstName: string;
  lastName: string;
  cvPdfKey: string;
}

export default function PublicCvUpload() {
  const api = usePublicApiClient();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Impostazioni pubbliche del flusso CV: da qui arriva l'URL dell'informativa
  // privacy da mostrare nel checkbox di consenso. In caricamento/errore si usa
  // il default esportato dagli shared DTO.
  const { data: publicCvSettings } = useQuery<PublicCvSettingsDto>({
    queryKey: ['public-cv-settings'],
    queryFn: () => api.get<PublicCvSettingsDto>('/api/settings/public-cv'),
  });
  const privacyPolicyUrl =
    publicCvSettings?.privacyPolicyUrl ?? DEFAULT_PUBLIC_CV_PRIVACY_POLICY_URL;
  // Logo opzionale configurato dall'admin, mostrato centrato in cima al form.
  const logoUrl = publicCvSettings?.logoUrl ?? '';
  // Personalizzazioni opzionali della pagina pubblica.
  const backgroundUrl = publicCvSettings?.backgroundUrl ?? '';
  const primaryColor = publicCvSettings?.primaryColor ?? '';

  // Stile inline della shell pubblica: override del colore primario
  // (sovrascrive le custom properties del tema limitatamente a questa pagina).
  // Le varianti hover/active derivano dallo stesso colore tramite `color-mix`,
  // così restano coerenti.
  const mainStyle: CSSProperties = {};
  if (primaryColor !== '') {
    const vars = mainStyle as Record<string, string>;
    vars['--color-primary'] = primaryColor;
    vars['--color-primary-hover'] = `color-mix(in srgb, ${primaryColor} 85%, black)`;
    vars['--color-primary-active'] = `color-mix(in srgb, ${primaryColor} 70%, black)`;
    vars['--color-primary-soft'] = `color-mix(in srgb, ${primaryColor} 12%, white)`;
    vars['--color-primary-contrast'] = '#ffffff';
  }

  // Lo sfondo custom deve SOVRASCRIVERE quello di default dell'app (che vive
  // sull'`.app-shell` in `cover`, full-screen), non aggiungere un layer sopra:
  // così footer e navbar restano al loro posto e, se nessuno sfondo è
  // impostato, resta visibile quello di default. Si agisce sulla custom
  // property `--app-bg-image` dell'`.app-shell`, ripristinata all'unmount.
  useEffect(() => {
    if (backgroundUrl === '') return;
    const shell = document.querySelector<HTMLElement>('.public-shell');
    if (!shell) return;
    const previous = shell.style.getPropertyValue('--app-bg-image');
    shell.style.setProperty(
      '--app-bg-image',
      `url(${JSON.stringify(backgroundUrl)})`,
    );
    return () => {
      // Ripristina il valore precedente (di solito vuoto → torna al default CSS).
      if (previous === '') shell.style.removeProperty('--app-bg-image');
      else shell.style.setProperty('--app-bg-image', previous);
    };
  }, [backgroundUrl]);

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const canSubmit =
    trimmedFirstName.length > 0 &&
    trimmedLastName.length > 0 &&
    pdfFile !== null &&
    privacyAccepted &&
    !isSubmitting;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pdfFile === null || !canSubmit) return;

    setIsSubmitting(true);
    setError(null);
    try {
      // 1) URL di upload firmato (solo application/pdf).
      const presigned = await api.post<PresignedUpload>(
        '/api/public/public-cv/uploads/presign',
        { contentType: PDF_CONTENT_TYPE },
      );

      // 2) PUT diretto su S3. NB: si usa `fetch` nativo (non il client API), che
      // non deve allegare alcun header oltre al `Content-Type`: S3 valida la
      // firma sul solo `Content-Type`, che deve combaciare con quello firmato.
      const putResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': presigned.contentType },
        body: pdfFile,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload non riuscito (HTTP ${putResponse.status})`);
      }

      // 3) Crea il profilo con la chiave del PDF caricato (avvia l'AI) e ottiene
      // il Public_Token opaco.
      const body: CreatePublicProfileBody = {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        cvPdfKey: presigned.key,
      };
      const result = await api.post<PublicCvCreateResult>(
        '/api/public/public-cv/profiles',
        body,
      );

      // 4) Naviga alla recap referenziando il profilo tramite il Public_Token
      // (mai un id interno) (Req. 6.2).
      navigate(`/public/submission/${result.token}`);
    } catch {
      setError(
        'Invio del CV non riuscito. Controlla i dati e riprova tra qualche istante.',
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={
        backgroundUrl !== ''
          ? 'public-cv-upload public-cv-upload--has-background'
          : 'public-cv-upload'
      }
      style={mainStyle}
    >
      {/* Logo FUORI dal box del form (sopra il container). */}
      {logoUrl !== '' && (
        <div className="public-cv-upload__logo">
          <img src={logoUrl} alt="Logo" />
        </div>
      )}
      <div className="public-cv-upload__container">
      <PageHeader
        title="Carica il tuo CV"
        subtitle="Inserisci i tuoi dati e allega il CV in PDF: l'AI pre-compilerà il tuo profilo."
      />

      <form className="public-cv-upload__form" onSubmit={handleSubmit} noValidate>
        <Field id="public-cv-first-name" label="Nome">
          <TextInput
            id="public-cv-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Es. Mario"
            required
            disabled={isSubmitting}
          />
        </Field>

        <Field id="public-cv-last-name" label="Cognome">
          <TextInput
            id="public-cv-last-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Es. Rossi"
            required
            disabled={isSubmitting}
          />
        </Field>

        <Field id="public-cv-pdf" label="CV (PDF)">
          <input
            ref={fileInputRef}
            id="public-cv-pdf"
            type="file"
            accept={PDF_CONTENT_TYPE}
            required
            disabled={isSubmitting}
            onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        {/* Hint reso FUORI dal box del campo (sotto), in corsivo e più piccolo. */}
        <p className="public-cv-upload__field-note">
          Carica il tuo CV in formato PDF. Verrà analizzato dall'AI per
          pre-compilare il profilo.
        </p>

        {/* Consenso obbligatorio al trattamento dei dati: gate sul submit.
            Checkbox "grafico": l'input nativo resta per accessibilità ma è
            nascosto; l'indicatore visivo è lo `span.__box`. */}
        <label className="public-cv-upload__consent" htmlFor="public-cv-privacy">
          <input
            id="public-cv-privacy"
            className="public-cv-upload__consent-input"
            type="checkbox"
            checked={privacyAccepted}
            disabled={isSubmitting}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
          />
          <span className="public-cv-upload__consent-box" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
              <path
                d="M13.5 4.5 6.5 11.5 2.5 7.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="public-cv-upload__consent-text">
            Ho letto e accetto l'
            <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
              informativa sul trattamento dei dati personali
            </a>
            .
          </span>
        </label>

        {isSubmitting && (
          <Alert tone="info">Caricamento del CV e avvio della pre-compilazione AI in corso…</Alert>
        )}
        {error !== null && <Alert tone="error">{error}</Alert>}

        <Button
          type="submit"
          variant="primary"
          className="public-cv-upload__submit"
          disabled={!canSubmit}
        >
          Invia il CV
        </Button>
      </form>
      </div>
    </div>
  );
}
